import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Loader, ClipboardList, Stethoscope, ScanSearch, Sparkles, PenLine, Upload, Pin } from 'lucide-react';
import { templateApi, Template } from '../services/templateApi';
import { useAuth } from '../contexts/AuthContext';

// Category colour mapping for visual distinction
const CATEGORY_COLOURS: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  'Neurology':              { border: 'border-l-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
  'Orthopaedics':           { border: 'border-l-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'Psychiatry':             { border: 'border-l-teal-500', bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500' },
  'Pain Medicine':          { border: 'border-l-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  'General Surgery':        { border: 'border-l-red-500', bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  'Radiology':              { border: 'border-l-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'Cardiology':             { border: 'border-l-rose-500', bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-500' },
  'Obstetrics & Gynaecology': { border: 'border-l-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-700 dark:text-pink-300', dot: 'bg-pink-500' },
  'Respiratory Medicine':   { border: 'border-l-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'ENT':                    { border: 'border-l-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', dot: 'bg-indigo-500' },
  'Medical-Legal':          { border: 'border-l-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
  'Legal':                  { border: 'border-l-slate-500', bg: 'bg-slate-50 dark:bg-slate-900/20', text: 'text-slate-700 dark:text-slate-300', dot: 'bg-slate-500' },
  'Summary':                { border: 'border-l-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
  'Clinical':               { border: 'border-l-lime-500', bg: 'bg-lime-50 dark:bg-lime-900/20', text: 'text-lime-700 dark:text-lime-300', dot: 'bg-lime-500' },
  'Solicitor':              { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
};

const DEFAULT_COLOUR = { border: 'border-l-medical-400', bg: 'bg-medical-50 dark:bg-medical-800', text: 'text-medical-700 dark:text-medical-300', dot: 'bg-medical-400' };

function getCategoryColour(category: string) {
  return CATEGORY_COLOURS[category] || DEFAULT_COLOUR;
}

/** Infer report type from template name */
function getReportType(name: string): { label: string; icon: React.ReactNode } {
  const lower = name.toLowerCase();
  if (lower.includes('screening') || lower.includes('review')) {
    return { label: 'Screening', icon: <ScanSearch className="h-3.5 w-3.5" /> };
  }
  if (lower.includes('personal injury')) {
    return { label: 'Personal Injury', icon: <Stethoscope className="h-3.5 w-3.5" /> };
  }
  if (lower.includes('condition') || lower.includes('causation') || lower.includes('prognosis')) {
    return { label: 'Condition & Prognosis', icon: <ClipboardList className="h-3.5 w-3.5" /> };
  }
  return { label: 'Report', icon: <FileText className="h-3.5 w-3.5" /> };
}

export const TemplateListPage: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    user?.personaType === 'SOLICITOR' ? 'Solicitor' : 'all'
  );
  const navigate = useNavigate();

  // Sync filter when persona changes (e.g. user switches in Account settings).
  useEffect(() => {
    setSelectedCategory(user?.personaType === 'SOLICITOR' ? 'Solicitor' : 'all');
  }, [user?.personaType]);

  const { data: templates, isLoading, error } = useQuery(
    ['templates'],
    () => templateApi.getTemplates(),
    {
      staleTime: 30000,
    }
  );

  // Extract unique categories
  const categories = useMemo(() => {
    if (!templates) return [];
    const cats = [...new Set(templates.map(t => t.category || 'Uncategorised'))];
    return cats.sort();
  }, [templates]);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates?.filter(template => {
      const matchesSearch = !searchTerm ||
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;

      return matchesSearch && matchesCategory;
    }) || [];
  }, [templates, searchTerm, selectedCategory]);

  // Group by category
  const templatesByCategory = useMemo(() => {
    return filteredTemplates.reduce((acc, template) => {
      const category = template.category || 'Uncategorised';
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    }, {} as Record<string, Template[]>);
  }, [filteredTemplates]);

  // Count templates per category (unfiltered by category, but respecting search + type)
  const categoryCounts = useMemo(() => {
    const baseFiltered = templates?.filter(template => {
      const matchesSearch = !searchTerm ||
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    }) || [];

    const counts: Record<string, number> = { all: baseFiltered.length };
    baseFiltered.forEach(t => {
      const cat = t.category || 'Uncategorised';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [templates, searchTerm]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading templates...</span>
        </div>
      </div>
    );
  }

  if (error && !templates) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-medical-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Failed to load templates</h3>
            <p className="text-medical-600 dark:text-medical-400">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Templates</h1>
            <p className="text-medical-600 dark:text-medical-400 mt-1">
              Choose a template to draft professional medical-legal documents
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Link
              to="/templates/new?upload=true"
              className="btn-secondary flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload Document
            </Link>
            <Link
              to="/templates/new"
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Template
            </Link>
          </div>
        </div>

        {/* Search + Visibility Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400 dark:text-medical-500" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-medical-900 text-white dark:bg-medical-100 dark:text-medical-900'
                : 'bg-medical-100 text-medical-600 hover:bg-medical-200 dark:bg-medical-700 dark:text-medical-300 dark:hover:bg-medical-600'
            }`}
          >
            All Specialisms
            <span className="ml-1.5 opacity-70">{categoryCounts.all || 0}</span>
          </button>
          {categories.map((category) => {
            const colour = getCategoryColour(category);
            const isSelected = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  isSelected
                    ? `${colour.bg} ${colour.text} ring-1 ring-current`
                    : 'bg-medical-100 text-medical-600 hover:bg-medical-200 dark:bg-medical-700 dark:text-medical-300 dark:hover:bg-medical-600'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${colour.dot}`} />
                {category}
                <span className="opacity-70">{categoryCounts[category] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Templates */}
      {filteredTemplates.length === 0 ? (
        <div className="card">
          <div className="text-center py-16">
            <FileText className="h-16 w-16 text-medical-200 dark:text-medical-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No templates found</h3>
            <p className="text-medical-500 dark:text-medical-400 mb-6 max-w-md mx-auto">
              {searchTerm
                ? `No templates match "${searchTerm}". Try a different search term or clear your filters.`
                : 'Create your first template to get started.'}
            </p>
            {!searchTerm && (
              <Link to="/templates/new" className="btn-primary inline-flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create Template
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(templatesByCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, categoryTemplates]) => {
              const colour = getCategoryColour(category);
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${colour.dot}`} />
                    <h2 className="text-base font-semibold text-medical-900 dark:text-medical-100">
                      {category}
                    </h2>
                    <span className="text-xs text-medical-400 dark:text-medical-500">
                      {categoryTemplates.length} template{categoryTemplates.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryTemplates.map((template) => (
                      <TemplateCard key={template.id} template={template} />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

// Template Card Component
const TemplateCard: React.FC<{ template: Template }> = ({ template }) => {
  const navigate = useNavigate();
  const sections = templateApi.parseSections(template.sections);
  const colour = getCategoryColour(template.category);
  const reportType = getReportType(template.name);
  const aiCount = sections.filter(s => s.aiGenerated).length;
  const manualCount = sections.length - aiCount;

  return (
    <div
      className={`bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 border-l-4 ${colour.border} p-4 hover:shadow-md transition-all cursor-pointer group flex flex-col`}
      onClick={() => navigate(`/templates/${template.id}`)}
    >
      {/* Header: type badge + visibility */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colour.bg} ${colour.text}`}>
            {reportType.icon}
            {reportType.label}
          </span>
          {(template as any).hasStyleReference && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400" title="Has document template">
              <Pin className="h-3 w-3" />
              Doc
            </span>
          )}
        </div>
      </div>

      {/* Template name */}
      <h3 className="text-sm font-semibold text-medical-900 dark:text-medical-100 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors leading-snug">
        {template.name}
      </h3>

      {/* Section list */}
      <div className="flex-1 mb-3">
        <ol className="space-y-0.5">
          {sections.map((section, idx) => (
            <li key={section.id} className="flex items-center gap-1.5 text-xs text-medical-600 dark:text-medical-400">
              <span className="text-medical-300 dark:text-medical-600 w-3 text-right flex-shrink-0 tabular-nums">{idx + 1}</span>
              <span className="truncate">{section.title}</span>
              {section.aiGenerated && (
                <Sparkles className="h-3 w-3 text-amber-400 flex-shrink-0" />
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Footer stats */}
      <div className="flex items-center gap-3 pt-2 border-t border-medical-100 dark:border-medical-700 text-xs text-medical-400 dark:text-medical-500">
        <span className="inline-flex items-center gap-1">
          <PenLine className="h-3 w-3" />
          {manualCount} manual
        </span>
        {aiCount > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-500 dark:text-amber-400">
            <Sparkles className="h-3 w-3" />
            {aiCount} AI-assisted
          </span>
        )}
      </div>
    </div>
  );
};
